import type { ReactNode } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
} from "@mui/material";
import { useValidationBlur } from "../../hooks/useValidationBlur";

export const DropdownSelect = <T = string,>({
  label,
  value = "" as T,
  onChange,
  options,
  multiple = false,
  renderValue,
  inputRef,
  wrapperSx,
  sx,
  size = "medium",
  isValid = true,
  helperText,
  disabled = false,
}: {
  label?: string;
  name?: string;
  value?: T;
  onChange: (value: T) => void;
  options: { value: string; label: string }[] | string[];
  helperText?: string;
  isValid?: boolean;
  inputRef?: (input: HTMLSelectElement | null) => void;
  disabled?: boolean;
  validationText?: string;
  multiple?: boolean;
  renderValue?: (value: T) => ReactNode;
  wrapperSx?: SxProps;
  sx?: SxProps;
  size?: "small" | "medium";
}) => {
  const { shouldValidate, onBlur } = useValidationBlur();

  const isInvalidState = !isValid && shouldValidate && !disabled;

  return (
    <FormControl sx={{ width: "100%", marginBottom: '16px', ...wrapperSx }}>
      <InputLabel>{label}</InputLabel>
      <Select<T>
        disabled={disabled}
        inputRef={inputRef}
        label={label}
        variant="outlined"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        size={size}
        MenuProps={{
          elevation: 16,
          PaperProps: {
            style: {
              maxHeight: 300, // Fixed height for 10 items (30px per item)
              overflow: 'auto',
            },
          },
        }}
        sx={sx}
        multiple={multiple}
        fullWidth
        className="hdg-select-input"
        error={isInvalidState}
        onBlur={onBlur}
        renderValue={renderValue}
      >
        {options?.map((option) => {
          const isString = typeof option === "string";
          const value = isString ? option : option.value;
          const label = isString ? option : option.label;
          return (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
